<script setup lang="ts">
import { computed, nextTick, ref, watch, type ComponentPublicInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  claimTaxYearConfig,
  useClaimTaxBack,
} from '@/composables/useClaimTaxBack'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import { donationsRouteForPeriod } from '@/helpers/givingRecord'
import { calculateClaimEstimateForBand } from '@/helpers/claimTaxBack/estimate'
import ClaimGuideStepIndex from '@/components/dashboard/claim/ClaimGuideStepIndex.vue'
import ClaimGuideMetrics from '@/components/dashboard/claim/ClaimGuideMetrics.vue'
import ClaimGuideCollapse from '@/components/dashboard/claim/ClaimGuideCollapse.vue'

/**
 * Guide step 2: build the claim from Gift Aided donations in the account.
 *
 * Recording happens on the giving record (the year list), not here, so the donor
 * can see what we already hold and avoid recording the same gift twice. The
 * live snapshot (giving, claimable, donation count) sits here. Recording
 * and confirming sit together at the bottom, so the donor cannot confirm
 * without seeing how to record anything missing. Confirming "That's all my
 * Gift Aided donations" collapses the step; the record link stays on the
 * summary so they can still update gifts later. When the account total is
 * £0 they can skip this step instead, so they can still see how to claim.
 * The step stays pending, like step 3, until tax details in step 1 are
 * complete and the donor can claim extra relief.
 */
const props = defineProps<{
  stepNumber: number
  totalGivenPence: number
  donationCount: number
  /** False until step 1 is complete for someone who can claim extra relief. */
  taxReady: boolean
}>()

const { t } = useI18n()
const { profile, confirmDonations } = useClaimTaxBack()

const yearLabel = claimTaxYearConfig.yearLabel
const donationsLink = donationsRouteForPeriod(claimTaxYearConfig.periodKey)

const isConfirmed = computed(() => !!profile.value?.donationsConfirmedAt)
const hasNoGiving = computed(() => props.totalGivenPence === 0)

const isExpanded = ref(props.taxReady && !isConfirmed.value)
const viewAllLink = ref<ComponentPublicInstance | null>(null)
const confirmError = ref<string | null>(null)
const isConfirming = ref(false)

watch(
  () => props.taxReady,
  (ready) => {
    if (ready && !isConfirmed.value) isExpanded.value = true
    if (!ready) isExpanded.value = false
  }
)

const collapse = async () => {
  isExpanded.value = false
  await nextTick()
  const el = viewAllLink.value?.$el
  if (el instanceof HTMLElement) el.focus()
}

const submitConfirm = async () => {
  confirmError.value = null
  isConfirming.value = true
  try {
    await confirmDonations()
    await collapse()
  } catch {
    confirmError.value = t('Dashboard.ClaimTaxBack.Guide.Donations.ErrorSave')
  } finally {
    isConfirming.value = false
  }
}

const totalDisplay = computed(() => formatMoneyFromPence(props.totalGivenPence))

const claimablePence = computed(() => {
  const bandId = profile.value?.incomeBandId
  if (!bandId) return null
  return calculateClaimEstimateForBand(
    props.totalGivenPence,
    bandId,
    claimTaxYearConfig
  ).claimablePence
})
const showClaimableAmount = computed(
  () => claimablePence.value != null || props.totalGivenPence === 0
)

const checkerGivingPence = computed(
  () => profile.value?.manualGivingPence ?? null
)

const comparison = computed<'shortfall' | 'ahead' | null>(() => {
  if (isConfirmed.value) return null
  const checker = checkerGivingPence.value
  if (checker === null || checker === props.totalGivenPence) return null
  return props.totalGivenPence < checker ? 'shortfall' : 'ahead'
})
</script>

<template>
  <section
    class="sa-claim-guide__step"
    :class="{ 'sa-claim-guide__step--collapsed': !isExpanded }"
    aria-labelledby="claim-guide-donations-heading"
  >
    <div class="sa-claim-guide__step-header">
      <ClaimGuideStepIndex
        :number="props.stepNumber"
        :done="isConfirmed"
        :pending="!props.taxReady"
      />
      <h3
        id="claim-guide-donations-heading"
        class="sa-claim-guide__step-title"
      >
        <span class="sa-sr-only">
          {{
            t('Dashboard.ClaimTaxBack.Guide.StepLabel', {
              number: props.stepNumber,
            })
          }}.
        </span>
        {{ t('Dashboard.ClaimTaxBack.Guide.Donations.Title') }}
        <span v-if="isConfirmed" class="sa-sr-only">
          . {{ t('Dashboard.ClaimTaxBack.Guide.StepDone') }}
        </span>
      </h3>
    </div>
    <Transition name="sa-claim-fade">
      <div v-if="!isExpanded && isConfirmed" class="sa-claim-guide__summary">
        <ClaimGuideMetrics
          :year-label="yearLabel"
          :given-display="totalDisplay"
          :claimable-pence="claimablePence"
          :show-claimable-amount="showClaimableAmount"
          :donation-count="props.donationCount"
        />
        <router-link
          ref="viewAllLink"
          class="sa-btn-secondary sa-btn-secondary--compact"
          :to="donationsLink"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Donations.ViewAll') }}
        </router-link>
      </div>
    </Transition>

    <div v-if="!props.taxReady" class="sa-claim-guide__step-main">
      <p class="sa-claim-guide__step-body">
        {{
          t('Dashboard.ClaimTaxBack.Guide.Donations.WaitingForTax.Body', {
            year: yearLabel,
          })
        }}
      </p>
    </div>

    <ClaimGuideCollapse
      :show="props.taxReady && isExpanded"
      id="claim-guide-donations-body"
    >
      <p class="sa-claim-guide__step-body">
        {{
          hasNoGiving
            ? t('Dashboard.ClaimTaxBack.Guide.Donations.ZeroBody', {
                year: yearLabel,
              })
            : t('Dashboard.ClaimTaxBack.Guide.Donations.Body', { year: yearLabel })
        }}
      </p>
      <ClaimGuideMetrics
        :year-label="yearLabel"
        :given-display="totalDisplay"
        :claimable-pence="claimablePence"
        :show-claimable-amount="showClaimableAmount"
        :donation-count="props.donationCount"
      />

      <p
        v-if="comparison === 'shortfall'"
        class="sa-claim-guide__compare sa-claim-guide__compare--shortfall"
      >
        {{
          t('Dashboard.ClaimTaxBack.Guide.Donations.CompareShortfall', {
            estimate: formatMoneyFromPence(checkerGivingPence!),
            account: totalDisplay,
          })
        }}
      </p>
      <p
        v-else-if="comparison === 'ahead'"
        class="sa-claim-guide__compare sa-claim-guide__compare--ahead"
      >
        {{
          t('Dashboard.ClaimTaxBack.Guide.Donations.CompareAhead', {
            estimate: formatMoneyFromPence(checkerGivingPence!),
          })
        }}
      </p>

      <p v-if="confirmError" class="sa-field-error" role="alert">
        {{ confirmError }}
      </p>

      <div class="sa-claim-guide__donation-actions">
        <router-link
          class="sa-btn-secondary sa-btn-secondary--compact"
          :to="donationsLink"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Donations.ViewAll') }}
        </router-link>
        <button
          v-if="!isConfirmed"
          type="button"
          class="sa-btn-primary sa-btn-primary--compact sa-claim-guide__confirm"
          :disabled="isConfirming"
          @click="submitConfirm"
        >
          {{
            hasNoGiving
              ? t('Dashboard.ClaimTaxBack.Guide.Donations.Skip')
              : t('Dashboard.ClaimTaxBack.Guide.Donations.Confirm')
          }}
        </button>
      </div>
    </ClaimGuideCollapse>
  </section>
</template>
